import React, { useRef } from "react";
import { Grid, IconButton } from "@mui/material";
import TextInput from "controls/global/text-input-field/TextInput";
import EditFilledIcon from "theme/icons/EditFilledIcon";
import UpdateFileNumberDialog from "./UpdateFileNumberDialog";
import { DialogRefType } from "entities/UIModel";
import { TitleSearchProduct } from "entities/UIModel";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import ErrorDialog from "controls/global/dialogs/error-dialog";
import { errorMessageArray } from "pages/file/utils/helper";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";

type ProductDetailsProps = {
  orderCreatedDate?: string;
  externalFileNumber?: string;
  orderStatusTypeName?: string;
  handleRefresh?: () => void;
  isFieldDisabled?: boolean;
  currentProduct: TitleSearchProduct;
};

const ERROR_MESSAGE = "Stewart File Number Update Failed, please try again or contact support for assistance.";

export const errorMessageContent = (
  errorMessages: string[],
  errorHeader: string,
): React.ReactNode => {
  const renderHTML = (htmlString: string) => ({ __html: htmlString });

  if (errorHeader.length > 0) {
    return (
      <>
        <div dangerouslySetInnerHTML={renderHTML(errorHeader)} />
        <div style={{ textAlign: "center" }}>
          {errorMessages?.map((e) => (
            <div key={e} dangerouslySetInnerHTML={renderHTML(e)} />
          ))}
        </div>
      </>
    );
  } else {
    if (errorMessages.length > 1) {
      return (
        <div style={{ textAlign: "center" }}>
          {errorMessages?.map((e) => (
            <div key={e} dangerouslySetInnerHTML={renderHTML(e)} />
          ))}
        </div>
      );
    } else {
      return <span dangerouslySetInnerHTML={renderHTML(errorMessages[0])} />;
    }
  }
};

const ProductStatus = (props: ProductDetailsProps) => {
  const updateFileNumberDialogRef = useRef<DialogRefType>(null);
  const [{ profileSettings }] = useProfileSettingsCache();
  const { generalConfig } = useConfigContext();  
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const [showErrorDialog, setShowErrorDialog] = React.useState(false);
  const isReadOnly = useFieldDisabler('TSEditExternalFileNumber');

  return (
    <>
      <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
        <TextInput
          name={props.orderCreatedDate}
          label="Ordered Date/Time"
          disabled
          value={props.orderCreatedDate}
        />
      </Grid>
      <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
        <TextInput
          name={props.externalFileNumber}
          label="Stewart Order Number"
          disabled
          value={props.externalFileNumber}
          InputProps={
            permissionCodes.includes("TITLESEARCH_EDITORDERNUMBER") &&
            profileSettings?.isStaffUser
              ? {
                  endAdornment: (
                    <IconButton
                      disabled={isReadOnly}
                      onClick={() => {
                        updateFileNumberDialogRef.current?.open();
                      }}
                    >
                      <EditFilledIcon />
                    </IconButton>
                  ),
                }
              : undefined
          }
        />
      </Grid>
      <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
        <TextInput
          name={props.orderStatusTypeName}
          label="Status"
          disabled
          value={props.orderStatusTypeName}
        />
      </Grid>

      <UpdateFileNumberDialog
        ref={updateFileNumberDialogRef}
        handleRefresh={props.handleRefresh}
        currentProduct={props.currentProduct}
        setShowErrorDialog={setShowErrorDialog}
      />
      <ErrorDialog
        isOpen={showErrorDialog}
        confirmationMessage={errorMessageContent(errorMessageArray(generalConfig), ERROR_MESSAGE)}
        onYes={() => setShowErrorDialog(false)}
      />
    </>
  );
};

export default ProductStatus;
